import { CoreCarsComponent } from "@b2bportal/core-cars";
import { useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  FilterBags,
  FilterCancellation,
  FilterCarType,
  FilterPassenger,
  FilterPrice,
  FilterSpecification,
  FilterSuppliers,
  FilterListSort,
} from "@tenant/cars";
import { FilterDialogContainer, FilterDialogSection } from "@tenant/ui";
import { usePackageFilterDialog } from "./usePackageFilterDialog";
import styles from "@/overrides/cars/availability/components/FilterDialog/styles.module.scss";

const COMPONENT_KEY = CoreCarsComponent.FilterDialog;

/**
 * Package-specific filter dialog for mobile that uses incremental pricing
 */
export const PackageCarFilterDialog = () => {
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t } = useI18nContext();

  // Use package-specific hook instead of core useFilterDialog
  const {
    context: {
      priceRange,
      passenger,
      minimumBags,
      supplier,
      filterOptions,
      freeCancellation,
      specification,
      carType,
      filtersChanged,
      totalFilterCount,
      sortOption,
    },
    handlers: {
      updateValuesToMatchState,
      resetAllFilters,
      applyFilter,
      onCarTypeChange,
      onPriceChange,
      onPassengerChange,
      onMinimumBagsChange,
      onSuppliersChange,
      onFreeCancellationChange,
      onSpecificationChange,
      onSortChange,
    },
  } = usePackageFilterDialog();

  return (
    <FilterDialogContainer
      onClickDialogTrigger={updateValuesToMatchState}
      onClickReset={resetAllFilters}
      onClickApply={applyFilter}
      numberOfFilters={totalFilterCount}
      className={block}
      filtersChanged={filtersChanged}
    >
      <FilterDialogSection
        className={cn("sort")}
        title={t("sortBy")}
        content={
          <FilterListSort selectedSort={sortOption} onChange={onSortChange} />
        }
      />
      <FilterDialogSection
        title={t("core-cars.filters.type.title")}
        content={
          <FilterCarType selectedValues={carType} onChange={onCarTypeChange} />
        }
      />
      <FilterDialogSection
        title={t("core-cars.filters.rentalCompany.title")}
        content={
          <FilterSuppliers
            selectedValues={supplier}
            onChange={onSuppliersChange}
            options={filterOptions.suppliers}
          />
        }
      />
      <FilterDialogSection
        className="price"
        title={t("core-ui.filters.price.title")}
        content={
          <FilterPrice
            onChange={onPriceChange}
            range={filterOptions.priceRange ?? undefined}
            selectedRange={priceRange}
            label={t("core-ui.filters.price.title")}
          />
        }
      />
      <FilterDialogSection
        title={t("core-cars.filters.cancellation.title")}
        content={
          <FilterCancellation
            isFreeCancellationSelected={freeCancellation}
            onChange={onFreeCancellationChange}
          />
        }
      />
      <FilterDialogSection
        title={t("core-cars.filters.specifications.title")}
        content={
          <FilterSpecification
            selectedValues={specification}
            onChange={onSpecificationChange}
          />
        }
      />
      <FilterDialogSection
        title={t("core-cars.filters.passengers.title")}
        content={
          <FilterPassenger
            onChange={onPassengerChange}
            selectedOption={passenger}
          />
        }
      />
      <FilterDialogSection
        title={t("core-cars.filtersDialog.minimumBags.title")}
        content={
          <FilterBags
            onChange={onMinimumBagsChange}
            minimumBags={minimumBags}
          />
        }
      />
    </FilterDialogContainer>
  );
};
